// src/components/monitoring/KpiCard.tsx
import React from 'react';
import { Target, TrendingUp, AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import { PpeppKpi } from '../../types';

interface Props {
    kpi: PpeppKpi;
    onEdit?: (kpi: PpeppKpi) => void;
}

const STATUS_CONFIG: Record<PpeppKpi['status'], { label: string; color: string; bg: string; icon: React.ReactNode }> = {
    on_track: { label: 'On Track', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', icon: <TrendingUp className="w-4 h-4 text-emerald-600" /> },
    warning: { label: 'Warning', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: <AlertTriangle className="w-4 h-4 text-amber-600" /> },
    overdue: { label: 'Overdue', color: 'text-red-600', bg: 'bg-red-50 border-red-200', icon: <Clock className="w-4 h-4 text-red-600" /> },
    achieved: { label: 'Achieved', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', icon: <CheckCircle className="w-4 h-4 text-blue-600" /> },
};

const KpiCard: React.FC<Props> = ({ kpi, onEdit }) => {
    const config = STATUS_CONFIG[kpi.status];

    // Calculate progress percentage (simple numeric comparison)
    const target = parseFloat(kpi.target_value) || 0;
    const current = parseFloat(kpi.current_value || '0') || 0;
    const progressPct = target > 0 ? Math.min(Math.round((current / target) * 100), 100) : 0;

    const progressColor = kpi.status === 'achieved' ? 'bg-blue-500'
        : kpi.status === 'on_track' ? 'bg-emerald-500'
            : kpi.status === 'warning' ? 'bg-amber-500'
                : 'bg-red-500';

    return (
        <div
            className={`border rounded-xl p-4 ${config.bg} hover:shadow-sm transition-shadow cursor-pointer`}
            onClick={() => onEdit?.(kpi)}
        >
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.bg}`}>
                        {config.icon}
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-neutral-text-primary">{kpi.nama_kpi}</h4>
                        {kpi.unit_of_measure && (
                            <span className="text-xs text-neutral-text-secondary">{kpi.unit_of_measure}</span>
                        )}
                    </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${config.color} ${config.bg}`}>
                    {config.icon} {config.label}
                </span>
            </div>

            {/* Values */}
            <div className="flex items-end gap-4 mb-3">
                <div>
                    <p className="text-xs text-neutral-text-secondary">Saat Ini</p>
                    <p className="text-lg font-black text-neutral-text-primary">{kpi.current_value || '—'}</p>
                </div>
                <div className="text-center">
                    <Target className="w-3.5 h-3.5 text-neutral-text-secondary mx-auto mb-0.5" />
                </div>
                <div>
                    <p className="text-xs text-neutral-text-secondary">Target</p>
                    <p className="text-lg font-black text-neutral-text-primary">{kpi.target_value}</p>
                </div>
                <div className="flex-1 text-right">
                    <p className={`text-2xl font-black ${config.color}`}>{progressPct}%</p>
                </div>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-white/60 rounded-full h-2 overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-500 ${progressColor}`}
                    style={{ width: `${progressPct}%` }}
                />
            </div>

            {/* Footer */}
            {(kpi.due_date || kpi.measurement_method) && (
                <div className="flex items-center justify-between mt-3 text-xs text-neutral-text-secondary">
                    {kpi.measurement_method && <span>Metode: {kpi.measurement_method}</span>}
                    {kpi.due_date && (
                        <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(kpi.due_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default KpiCard;
